/** Sent when a practitioner scope must match nobody rather than fall through to "all". */
export const NO_PRACTITIONER_MATCH_ID = '00000000-0000-0000-0000-000000000000';

export type PractitionerRoleRef = {
  id: string;
  system_key: string | null;
  is_practitioner: boolean;
};

export type PractitionerMemberRef = {
  role: 'main_clinic_admin' | 'clinic_admin' | 'employee';
  custom_role_id: string | null;
};

export function memberRoleIsPractitioner(
  member: PractitionerMemberRef,
  roles: PractitionerRoleRef[],
): boolean {
  if (member.role === 'clinic_admin' || member.role === 'main_clinic_admin') {
    return roles.some((role) => role.system_key === 'clinic_admin' && role.is_practitioner);
  }
  if (member.custom_role_id) {
    return roles.some((role) => role.id === member.custom_role_id && role.is_practitioner);
  }
  return roles.some((role) => role.system_key === 'employee' && role.is_practitioner);
}

export function filterPractitionerMembers<T extends PractitionerMemberRef>(
  members: T[],
  roles: PractitionerRoleRef[],
): T[] {
  return members.filter((member) => memberRoleIsPractitioner(member, roles));
}

/**
 * Who Ny aftale may preselect as Behandler. At most one person.
 *
 * 1. The logged-in user, when their role has the Behandler flag.
 * 2. Otherwise the patient's only linked Behandler under tilknytning.
 * 3. Otherwise nobody. An admin or secretary is never kept because the
 *    calendar was filtered to them, and a clicked column is not a selection.
 *
 * Null while roles, members, the session, or tilknytninger are still loading.
 * An empty list means the form should stay blank. Bloker tid does not use this.
 */
export function preselectedAppointmentPractitionerIds<
  T extends PractitionerMemberRef & { user_id: string },
>(input: {
  loggedInUserId: string | null | undefined;
  /** False while auth is still initializing and the user id is unknown. */
  userReady?: boolean;
  members: readonly T[] | null | undefined;
  roles: readonly PractitionerRoleRef[] | null | undefined;
  rolesReady: boolean;
  /**
   * Staff linked to the chosen patient. Null while that list is loading.
   * Omit or pass [] when no patient is chosen. Non-behandlere are ignored.
   */
  linkedPractitionerIds?: readonly string[] | null;
  /** When set, a candidate outside this set is not selected. */
  selectableIds?: ReadonlySet<string> | null;
}): string[] | null {
  if (!input.rolesReady || input.members == null) return null;
  if (input.userReady === false && !input.loggedInUserId) return null;

  const roles = [...(input.roles ?? [])];
  const isBehandler = (id: string | null | undefined) => {
    if (!id) return false;
    const member = input.members?.find((row) => row.user_id === id);
    return Boolean(member && memberRoleIsPractitioner(member, roles));
  };
  const allowed = (id: string) => input.selectableIds == null || input.selectableIds.has(id);

  if (isBehandler(input.loggedInUserId)) {
    const id = input.loggedInUserId as string;
    return allowed(id) ? [id] : [];
  }

  // Omitted means no patient, so there is nothing to wait for. Null means the
  // tilknytning query is still loading.
  const linked = input.linkedPractitionerIds === undefined ? [] : input.linkedPractitionerIds;
  if (linked == null) return null;

  const flagged: string[] = [];
  const seen = new Set<string>();
  for (const id of linked) {
    if (!id || seen.has(id) || !isBehandler(id)) continue;
    seen.add(id);
    flagged.push(id);
  }
  if (flagged.length === 1 && allowed(flagged[0])) return [flagged[0]];
  return [];
}

/**
 * Practitioner-role members, plus anyone already selected who is not flagged.
 * Same sticky rule as Henvisning → Tildeling: editing must not drop them.
 */
export function withStickySelectedMembers<T extends { user_id: string }>(
  practitioners: readonly T[],
  members: readonly T[],
  selectedIds: readonly (string | null | undefined)[],
): T[] {
  const selected = new Set(selectedIds.filter((id): id is string => Boolean(id)));
  if (selected.size === 0) return [...practitioners];
  const present = new Set(practitioners.map((member) => member.user_id));
  const sticky = members.filter(
    (member) => selected.has(member.user_id) && !present.has(member.user_id),
  );
  return [...practitioners, ...sticky];
}

/** Ids from `optionIds` that are flagged practitioners or already chosen. Null while the flag list is still loading. */
export function visiblePractitionerIds(
  optionIds: readonly string[],
  practitionerIds: readonly string[] | null,
  stickyIds: readonly (string | null | undefined)[] = [],
): string[] {
  if (practitionerIds == null) return [];
  const allowed = new Set<string>([
    ...practitionerIds,
    ...stickyIds.filter((id): id is string => Boolean(id)),
  ]);
  return optionIds.filter((id) => allowed.has(id));
}
